import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { format } from 'date-fns'
import {
  Root,
  Trigger,
  Portal,
  Content,
  Arrow,
  Provider,
} from '@radix-ui/react-tooltip'
import { sortOrderStatusHistory } from '../../common/orders/utils'
import T from './T'
import { orderStatuses } from '../../common/config'

const DEFAULT_DELAY = 200
const ANIMATION_DURATION = 0.2

const showUp = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

const hideAway = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0);
  }
`

const StyledContent = styled(Content)`
  background-color: white;
  box-shadow: 0px 2px 32px 11px rgb(0 0 0 / 26%);
  padding: 0;
  border-radius: 8px;
  border: none;
  font-size: 1.4rem;
  z-index: 10000;
  animation: ${showUp} ${ANIMATION_DURATION}s ease-in-out forwards;
  transform-origin: var(--radix-tooltip-content-transform-origin);
  max-width: 330px;

  &[data-state='closed'] {
    animation: ${hideAway} ${ANIMATION_DURATION}s ease-in-out;
  }
`

const StyledArrow = styled(Arrow)`
  fill: white;
`

const ChildrenWrapper = styled.span`
  cursor: pointer;
  display: inline-flex;
`

const PopupContainer = styled.div`
  min-width: 330px;
  overflow: hidden;
  border-radius: 8px;
`

const Title = styled.div`
  font-weight: 500;
  font-size: 1.5rem;
  line-height: 18px;
  color: black;
  text-align: center;
  padding: 14px;
`

const StatusRow = styled.div`
  font-weight: 400;
  font-size: 1.4rem;
  color: black;
  text-align: left;
  padding: 5px 16px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;

  &:nth-child(odd) {
    background-color: #f1f1f1;
  }
`
const StatusRowsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`

const DatetimeStamp = styled.div`
  font-weight: 600;
  width: 130px;
`
const StatusText = styled.div`
  ${({ isSubStatus, isFutureStatus, theme: { colors } }) => css`
    padding-left: ${isSubStatus ? '14px' : '4px'};
    font-style: ${isSubStatus && 'italic'};
    color: ${isFutureStatus && colors.orange};
  `}
`

const StatusHistoryTooltip = ({ children, order }) => {
  const sortedStatusHistory = sortOrderStatusHistory(order)

  if (!sortedStatusHistory?.length) {
    return children
  }

  const TooltipContent = () => (
    <PopupContainer>
      <Title>
        <T>Status history</T>
      </Title>
      <StatusRowsContainer>
        {sortedStatusHistory.map((history, index) => (
          <StatusRow key={index}>
            <DatetimeStamp>
              {format(new Date(history.statusAt), 'd. M. yyyy HH:mm')}
            </DatetimeStamp>
            <StatusText isSubStatus={history.isSubStatus}>
              <T>{history.statusName}</T>
            </StatusText>
          </StatusRow>
        ))}
        {order.estimatedDepartureAt &&
          order.status === orderStatuses.AT_MANUFACTURER && (
            <StatusRow>
              <DatetimeStamp>
                {format(new Date(order.estimatedDepartureAt), 'd. M. yyyy')}
              </DatetimeStamp>
              <StatusText isFutureStatus>
                <T>Estimated departure time</T>
              </StatusText>
            </StatusRow>
          )}
      </StatusRowsContainer>
    </PopupContainer>
  )

  return (
    <Provider>
      <Root delayDuration={DEFAULT_DELAY}>
        <Trigger asChild>
          <ChildrenWrapper data-id="status-history-tooltip">{children}</ChildrenWrapper>
        </Trigger>
        <Portal>
          <StyledContent side="bottom" sideOffset={10}>
            <TooltipContent />
            <StyledArrow width={11} height={5} />
          </StyledContent>
        </Portal>
      </Root>
    </Provider>
  )
}

export default StatusHistoryTooltip
